import { useState, useEffect, useRef, useCallback } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { GuidedInput } from "@/components/ui/guided-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Camera, Trash2, UserCircle, Contact, CalendarClock, Bell, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { apiFetch, uploadFile } from "@/lib/api";
import { useNavigation } from "@/contexts/NavigationContext";
import { SecurityPanel } from "@/components/security/SecurityPanel";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { UnsavedBar } from "@/components/UnsavedBar";
import { LoadingState } from "@/components/states";
import { WORKSPACES } from "@/lib/navRegistry";
import { cn } from "@/lib/utils";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMEZONES = ["Asia/Kolkata", "UTC", "America/New_York", "Europe/London"];
const AVATAR_ACCEPT = ".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp";

function Field({ label, children }) {
  return <div className="space-y-1.5"><label className="text-sm font-medium">{label}</label>{children}</div>;
}
function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <span className="text-sm text-foreground">{label}</span>{children}
    </div>
  );
}

function pick(p) {
  return {
    full_name: p.full_name || "", display_name: p.display_name || "",
    notification_email: p.notification_email || "", whatsapp: p.whatsapp || "",
    timezone: p.timezone || "Asia/Kolkata", working_days: p.working_days || [],
    working_hours: p.working_hours || "", preferred_channel: p.preferred_channel || "Email",
    availability_status: p.availability_status || "available",
    notification_prefs: p.notification_prefs || {}, landing: p.landing || "/",
  };
}

export default function MyProfile() {
  const { reload } = useNavigation();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    apiFetch("/profile/me").then((p) => { setProfile(p); setForm(pick(p)); }).catch((e) => toast.error(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = !!form && !!profile && JSON.stringify(form) !== JSON.stringify(pick(profile));
  useUnsavedChanges(dirty);

  if (!form) return <LoadingState label="Loading your profile…" />;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const np = form.notification_prefs || {};
  const setChannel = (k, v) => set("notification_prefs", { ...np, channels: { ...(np.channels || {}), [k]: v } });
  const setLevel = (k, v) => set("notification_prefs", { ...np, levels: { ...(np.levels || {}), [k]: v } });
  const setQuiet = (k, v) => set("notification_prefs", { ...np, quiet_hours: { ...(np.quiet_hours || {}), [k]: v } });
  const toggleDay = (d) => set("working_days", form.working_days.includes(d) ? form.working_days.filter((x) => x !== d) : [...form.working_days, d]);

  const save = async () => {
    const snapshot = form;
    setSaving(true);
    setProfile((p) => ({ ...p, ...snapshot }));  // optimistic: clears the unsaved bar instantly
    try {
      const updated = await apiFetch("/profile/me", { method: "PUT", body: JSON.stringify(snapshot) });
      setProfile(updated); setForm(pick(updated));
      toast.success("Your profile has been saved");
    } catch (e) { toast.error(e.message || "We couldn’t save your changes. Please try again."); }
    finally { setSaving(false); }
  };
  const discard = () => setForm(pick(profile));

  const onAvatar = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setAvatarBusy(true);
    try {
      const res = await uploadFile("/profile/avatar", file);
      setProfile((p) => ({ ...p, avatar_url: res.avatar_url }));
      reload(); toast.success("Profile photo updated");
    } catch (err) { toast.error(err.message); } finally { setAvatarBusy(false); }
  };
  const removeAvatar = async () => {
    setAvatarBusy(true);
    try { await apiFetch("/profile/avatar", { method: "DELETE" }); setProfile((p) => ({ ...p, avatar_url: null })); reload(); toast.success("Profile photo removed"); }
    catch (err) { toast.error(err.message); } finally { setAvatarBusy(false); }
  };

  const initials = (form.full_name || profile.login_email || "N").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="animate-fade-up pb-4">
      <PageHeader eyebrow="Your account" title="My Profile" subtitle="Manage your personal details, schedule and notifications." />

      <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
        {/* Left: avatar + identity + admin-controlled (read-only) */}
        <div className="space-y-6">
          <Card className="p-6 text-center shadow-soft" data-testid="profile-identity">
            <div className="relative mx-auto h-24 w-24">
              {profile.avatar_url
                ? <img src={profile.avatar_url} alt="" className="h-24 w-24 rounded-full object-cover" data-testid="my-avatar" />
                : <span className="flex h-24 w-24 items-center justify-center rounded-full bg-primary text-2xl font-semibold text-primary-foreground" data-testid="my-avatar-initials">{initials}</span>}
              <button onClick={() => fileRef.current?.click()} disabled={avatarBusy}
                className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full bg-card shadow-lift ring-1 ring-border transition-colors hover:bg-muted"
                data-testid="avatar-upload" aria-label="Change profile photo">
                {avatarBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4 text-foreground" />}
              </button>
              <input ref={fileRef} type="file" accept={AVATAR_ACCEPT} className="hidden" onChange={onAvatar} data-testid="avatar-input" />
            </div>
            <h3 className="mt-4 font-heading text-xl font-semibold">{form.full_name || "Your name"}</h3>
            <p className="text-sm text-muted-foreground">{profile.login_email}</p>
            {profile.avatar_url && (
              <Button variant="ghost" size="sm" className="mt-2 text-destructive hover:text-destructive" onClick={removeAvatar} disabled={avatarBusy} data-testid="avatar-remove">
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove photo
              </Button>
            )}
          </Card>

          <Card className="p-6 shadow-soft" data-testid="profile-admin-managed">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium"><ShieldCheck className="h-4 w-4 text-accent" /> Role &amp; reporting</div>
            <div className="space-y-3 text-sm">
              <div><div className="text-xs text-muted-foreground">Primary role</div><div className="font-medium">{profile.role || "—"}</div></div>
              <div><div className="text-xs text-muted-foreground">Additional roles</div><div className="font-medium">{(profile.additional_roles || []).join(", ") || "—"}</div></div>
              <div><div className="text-xs text-muted-foreground">Reporting manager</div><div className="font-medium">{profile.reporting_manager || "—"}</div></div>
            </div>
            <p className="mt-4 rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
              These are managed by an administrator and can’t be changed here.
            </p>
          </Card>
        </div>

        {/* Right: editable sections with progressive disclosure */}
        <Card className="p-6 shadow-soft">
          <Tabs defaultValue="basics">
            <TabsList className="mb-6 flex-wrap" data-testid="profile-tabs">
              <TabsTrigger value="basics" data-testid="tab-basics"><UserCircle className="mr-1.5 h-4 w-4" /> Basics</TabsTrigger>
              <TabsTrigger value="contact" data-testid="tab-contact"><Contact className="mr-1.5 h-4 w-4" /> Contact</TabsTrigger>
              <TabsTrigger value="schedule" data-testid="tab-schedule"><CalendarClock className="mr-1.5 h-4 w-4" /> Schedule</TabsTrigger>
              <TabsTrigger value="notifications" data-testid="tab-notifications"><Bell className="mr-1.5 h-4 w-4" /> Notifications</TabsTrigger>
              <TabsTrigger value="security" data-testid="tab-security"><KeyRound className="mr-1.5 h-4 w-4" /> Security</TabsTrigger>
            </TabsList>

            <TabsContent value="basics" className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <GuidedInput label="Full name" dataTestId="pf-full-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} helperText="Your name as shown across the workspace." />
                <GuidedInput label="Display name" dataTestId="pf-display-name" value={form.display_name} onChange={(e) => set("display_name", e.target.value)} helperText="A shorter name for greetings." />
              </div>
              <Field label="Login email"><div className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground" data-testid="pf-login-email">{profile.login_email}</div></Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Time zone">
                  <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
                    <SelectTrigger data-testid="pf-timezone"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Personal landing page">
                  <Select value={form.landing} onValueChange={(v) => set("landing", v)}>
                    <SelectTrigger data-testid="pf-landing"><SelectValue /></SelectTrigger>
                    <SelectContent>{WORKSPACES.map((w) => <SelectItem key={w.route} value={w.route}>{w.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
            </TabsContent>

            <TabsContent value="contact" className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <GuidedInput label="Notification email" type="email" dataTestId="pf-notif-email" value={form.notification_email} onChange={(e) => set("notification_email", e.target.value)} helperText="Where we send your reminders." />
                <GuidedInput label="WhatsApp / mobile" dataTestId="pf-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} helperText="For future WhatsApp reminders." exampleText="+91 …" />
              </div>
              <Field label="Preferred channel">
                <Select value={form.preferred_channel} onValueChange={(v) => set("preferred_channel", v)}>
                  <SelectTrigger data-testid="pf-channel"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Email">Email</SelectItem><SelectItem value="WhatsApp">WhatsApp</SelectItem><SelectItem value="In-app">In-app</SelectItem></SelectContent>
                </Select>
              </Field>
            </TabsContent>

            <TabsContent value="schedule" className="space-y-5">
              <Field label="Working days">
                <div className="flex flex-wrap gap-2" data-testid="pf-working-days">
                  {DAYS.map((d) => {
                    const on = form.working_days.includes(d);
                    return <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`pf-day-${d}`}
                      className={cn("rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                        on ? "border-accent bg-accent/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground")}>{d}</button>;
                  })}
                </div>
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <GuidedInput label="Working hours" dataTestId="pf-working-hours" value={form.working_hours} onChange={(e) => set("working_hours", e.target.value)} helperText="Optional." exampleText="9:00 AM – 6:00 PM" />
                <Field label="Availability">
                  <Select value={form.availability_status} onValueChange={(v) => set("availability_status", v)}>
                    <SelectTrigger data-testid="pf-availability"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="available">Available</SelectItem><SelectItem value="busy">Busy</SelectItem><SelectItem value="away">Away</SelectItem></SelectContent>
                  </Select>
                </Field>
              </div>
              <p className="rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">Leave dates and delegation will arrive in a later release.</p>
            </TabsContent>

            <TabsContent value="notifications" className="space-y-6">
              <div>
                <div className="mb-1 text-sm font-medium">Channels</div>
                <p className="mb-2 text-xs text-muted-foreground">How you’d like to be reached. WhatsApp sending arrives with a later release.</p>
                <Row label="In-app"><Switch checked={!!(np.channels?.in_app)} onCheckedChange={(v) => setChannel("in_app", v)} data-testid="pf-ch-in_app" /></Row>
                <Row label="Email"><Switch checked={!!(np.channels?.email)} onCheckedChange={(v) => setChannel("email", v)} data-testid="pf-ch-email" /></Row>
                <Row label="WhatsApp"><Switch checked={!!(np.channels?.whatsapp)} onCheckedChange={(v) => setChannel("whatsapp", v)} data-testid="pf-ch-whatsapp" /></Row>
              </div>
              <div>
                <div className="mb-2 text-sm font-medium">What to notify me about</div>
                <Row label="Critical"><Switch checked={!!(np.levels?.critical)} onCheckedChange={(v) => setLevel("critical", v)} data-testid="pf-lv-critical" /></Row>
                <Row label="Important"><Switch checked={!!(np.levels?.important)} onCheckedChange={(v) => setLevel("important", v)} data-testid="pf-lv-important" /></Row>
                <Row label="Routine"><Switch checked={!!(np.levels?.routine)} onCheckedChange={(v) => setLevel("routine", v)} data-testid="pf-lv-routine" /></Row>
              </div>
              <div>
                <Row label="Quiet hours"><Switch checked={!!(np.quiet_hours?.enabled)} onCheckedChange={(v) => setQuiet("enabled", v)} data-testid="pf-quiet-enabled" /></Row>
                {np.quiet_hours?.enabled && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="From"><input type="time" value={np.quiet_hours?.start || "22:00"} onChange={(e) => setQuiet("start", e.target.value)} className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" data-testid="pf-quiet-start" /></Field>
                    <Field label="To"><input type="time" value={np.quiet_hours?.end || "07:00"} onChange={(e) => setQuiet("end", e.target.value)} className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" data-testid="pf-quiet-end" /></Field>
                  </div>
                )}
                <p className="mt-2 text-xs text-muted-foreground">Quiet hours use your time zone: {form.timezone}.</p>
              </div>
            </TabsContent>

            <TabsContent value="security">
              <SecurityPanel email={profile.login_email} />
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      <UnsavedBar show={dirty} saving={saving} onSave={save} onDiscard={discard} />
    </div>
  );
}
